import React from 'react';
import { countries } from '../data/countries';
import TurnstileWidget from './TurnstileWidget';
import CountryCombobox from './CountryCombobox';

type Language = 'en' | 'es';

interface Translations {
  welcomeTitle: string;
  welcomeDescription: string;
  consentNotice: string;
  turnstileLabel: string;
  turnstileHint: string;
  startButton: string;
  languageLabel: string;
  countryLabel: string;
  countryPlaceholder: string;
  countryNoResults: string;
}

interface TranslationsMap {
  en: Translations;
  es: Translations;
}

const translations: TranslationsMap = {
  en: {
    welcomeTitle: 'Welcome to the Environment Management Survey',
    welcomeDescription: 'This survey will take approximately 5-10 minutes to complete. Your responses will help us understand the challenges and needs related to environment management in Drupal + frontend projects.',
    consentNotice: 'By starting, you acknowledge the survey privacy notice.',
    turnstileLabel: 'Security verification',
    turnstileHint: 'Complete verification to enable survey start.',
    startButton: 'Start Survey',
    languageLabel: 'Select your language',
    countryLabel: 'Select your country',
    countryPlaceholder: 'Type to find your country',
    countryNoResults: 'No countries match your search.',
  },
  es: {
    welcomeTitle: 'Bienvenido a la Encuesta de Gestión de Environments',
    welcomeDescription: 'Esta encuesta tomará aproximadamente 5-10 minutos para completarse. Sus respuestas nos ayudará a comprender los desafíos y necesidades relacionados con la gestión de environments en proyectos Drupal + frontend.',
    consentNotice: 'Al comenzar, confirmas que leiste el aviso de privacidad de la encuesta.',
    turnstileLabel: 'Verificación de seguridad',
    turnstileHint: 'Completa la verificación para habilitar el inicio de la encuesta.',
    startButton: 'Comenzar Encuesta',
    languageLabel: 'Selecciona idioma',
    countryLabel: 'Selecciona tu país',
    countryPlaceholder: 'Escribe para buscar tu país',
    countryNoResults: 'No hay países que coincidan con tu búsqueda.',
  },
};

interface WelcomeBlockProps {
  language: Language;
  onStart: () => void;
  onLanguageChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onCountryChange: (countryCode: string) => void;
  selectedCountry: string;
  turnstileEnabled: boolean;
  turnstileSiteKey: string;
  turnstileToken: string;
  turnstileResetNonce: number;
  onTurnstileTokenChange: (token: string) => void;
  onTurnstileError: (errorCode?: string) => void;
}

function WelcomeBlock({
  language,
  onStart,
  onLanguageChange,
  onCountryChange,
  selectedCountry,
  turnstileEnabled,
  turnstileSiteKey,
  turnstileToken,
  turnstileResetNonce,
  onTurnstileTokenChange,
  onTurnstileError,
}: WelcomeBlockProps): React.ReactElement {
  const t = translations[language];

  return (
    <div className="welcome-block">
      <div className="welcome-block__content">
        <h2 className="welcome-block__title">{t.welcomeTitle}</h2>
        <p className="welcome-block__description">{t.welcomeDescription}</p>
        <p className="welcome-block__consent">{t.consentNotice}</p>

        <div className="welcome-block__language-selector">
          <label className="welcome-block__language-label">{t.languageLabel}:</label>
          <select
            className="welcome-block__language-select"
            value={language}
            onChange={onLanguageChange}
          >
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </div>

        <div className="welcome-block__language-selector">
          <CountryCombobox
            id="country-input"
            label={t.countryLabel}
            placeholder={t.countryPlaceholder}
            noResultsText={t.countryNoResults}
            countries={countries}
            selectedCountry={selectedCountry}
            onCountryChange={onCountryChange}
          />
        </div>

        <button
          className="welcome-block__button"
          onClick={onStart}
          disabled={!selectedCountry || (turnstileEnabled && !turnstileToken)}
        >
          {t.startButton}
        </button>

        {turnstileEnabled && (
          <div className="welcome-block__turnstile-wrapper">
            <p className="welcome-block__turnstile-label">{t.turnstileLabel}</p>
            <TurnstileWidget
              siteKey={turnstileSiteKey}
              language={language}
              resetNonce={turnstileResetNonce}
              onTokenChange={onTurnstileTokenChange}
              onWidgetError={onTurnstileError}
            />
            {!turnstileToken && <p className="welcome-block__turnstile-hint">{t.turnstileHint}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export default WelcomeBlock;
